import React, { useState } from "react";
import { X, Building2, ChevronLeft, ChevronRight } from "lucide-react";
import { clinicPhotos } from "../mock";

export default function ClinicTour() {
  const [openIdx, setOpenIdx] = useState(null);

  const close = () => setOpenIdx(null);
  const prev = (e) => {
    e?.stopPropagation();
    setOpenIdx((i) => (i === null ? null : (i - 1 + clinicPhotos.length) % clinicPhotos.length));
  };
  const next = (e) => {
    e?.stopPropagation();
    setOpenIdx((i) => (i === null ? null : (i + 1) % clinicPhotos.length));
  };

  return (
    <section id="clinic-tour" className="relative px-4 md:px-8 py-20 md:py-28 bg-[#f0edfa] molecule-bg">
      <div className="mx-auto max-w-[1400px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-12">
          <div>
            <span className="eyebrow">Inside the clinic</span>
            <h2 className="section-title">A friendly, modern practice — take a look inside</h2>
          </div>
          <p className="text-[#4a4a63] leading-relaxed lg:max-w-lg lg:ml-auto">
            Bright waiting areas, fully equipped treatment rooms and on-site pathology — everything you need for great care in one convenient location on William Street.
          </p>
        </div>

        {/* Feature + grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Big feature (first photo) — spans 2 cols on desktop */}
          {clinicPhotos[0] && (
            <button
              onClick={() => setOpenIdx(0)}
              className="group relative rounded-[28px] overflow-hidden aspect-[4/3] lg:aspect-auto lg:row-span-2 lg:col-span-2 shadow-md hover:shadow-xl transition-shadow"
            >
              <img
                src={clinicPhotos[0].src}
                alt={clinicPhotos[0].label}
                style={{ filter: "contrast(1.05) saturate(1.08) brightness(1.02)" }}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-left">
                <div className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                  <Building2 className="w-3 h-3" /> {clinicPhotos[0].label}
                </div>
                <div className="text-white font-bold text-lg md:text-xl leading-snug max-w-lg">
                  {clinicPhotos[0].caption}
                </div>
              </div>
            </button>
          )}

          {/* Other photos — wraps into remaining column on desktop, 2-col grid on mobile */}
          {clinicPhotos.slice(1).map((p, idx) => (
            <button
              key={p.src + idx}
              onClick={() => setOpenIdx(idx + 1)}
              data-testid={`clinic-photo-${idx + 1}`}
              className="group relative rounded-[24px] overflow-hidden aspect-[4/3] shadow-md hover:shadow-xl transition-shadow"
            >
              <img
                src={p.src}
                alt={p.label}
                style={{ filter: "contrast(1.05) saturate(1.08) brightness(1.02)" }}
                className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-left">
                <div className="text-white font-bold text-sm md:text-base leading-tight">
                  {p.label}
                </div>
                <div className="text-white/80 text-xs mt-1 line-clamp-2">{p.caption}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {openIdx !== null && (
        <div
          onClick={close}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm grid place-items-center px-4 py-8 animate-in fade-in"
        >
          <button
            onClick={close}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <button
            onClick={prev}
            className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            aria-label="Previous"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={next}
            className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
            aria-label="Next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="relative max-w-[1100px] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={clinicPhotos[openIdx].src}
              alt={clinicPhotos[openIdx].label}
              className="w-full h-auto max-h-[80vh] object-contain rounded-2xl"
            />
            <div className="mt-4 text-center">
              <div className="inline-flex items-center gap-2 bg-[#3dc9b3] text-white text-xs font-bold px-3 py-1 rounded-full mb-2">
                {clinicPhotos[openIdx].label}
              </div>
              <div className="text-white font-medium">{clinicPhotos[openIdx].caption}</div>
              <div className="text-white/50 text-xs mt-1">{openIdx + 1} / {clinicPhotos.length}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
